import React, { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { authClient, useSession } from '@/lib/auth-client'
import { Spinner } from '@/components/ui/spinner'
import type { LoginRequest, RegisterRequest, User } from '../services/api'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<boolean>
  register: (data: RegisterRequest) => Promise<boolean>
  logout: () => Promise<void>
  updateUser: (data: Partial<User>) => Promise<boolean>
  checkAuth: () => Promise<void>
  isSwitchingBranch: boolean
  activeBranchId: string | null
  switchBranch: (organizationId: string) => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Map Better Auth's session user (+ active org member) to the existing
// frontend User shape.
//
// Role precedence:
//   1. Global `user.role === 'system_admin'` → 'admin' (technical/support; sees everything)
//   2. Active organization member's role → that role (admin/staff/teacher)
//   3. Fallback → 'staff' (conservative)
function mapSessionUser(
  sessionUser: any,
  activeMember: { role?: string } | null | undefined,
): User | null {
  if (!sessionUser) return null

  const globalRole = sessionUser.role as string | undefined
  const memberRole = activeMember?.role
  const allowedRoles = ['admin', 'staff', 'teacher'] as const

  let frontendRole: User['role'] = 'staff'
  if (globalRole === 'system_admin') {
    frontendRole = 'admin'
  } else if (memberRole && (allowedRoles as readonly string[]).includes(memberRole)) {
    frontendRole = memberRole as User['role']
  }

  const firstName = sessionUser.firstName ?? ''
  const lastName = sessionUser.lastName ?? ''
  const fullName = `${firstName} ${lastName}`.trim() || sessionUser.name || sessionUser.email

  let preferences: User['preferences'] = {
    theme: 'light',
    language: 'es',
    notifications: true,
  }
  if (typeof sessionUser.preferences === 'string' && sessionUser.preferences) {
    try {
      const parsed = JSON.parse(sessionUser.preferences)
      preferences = { ...preferences, ...parsed }
    } catch {
      // ignore malformed JSON
    }
  }

  return {
    id: sessionUser.id,
    userId: sessionUser.userId ?? sessionUser.id,
    firstName,
    lastName,
    fullName,
    email: sessionUser.email,
    role: frontendRole,
    status: (sessionUser.status as User['status']) ?? 'active',
    mustChangePassword: sessionUser.mustChangePassword === true,
    preferences,
    lastLogin: sessionUser.lastLogin,
    profilePicture: sessionUser.profilePicture ?? sessionUser.image,
    phone: sessionUser.phone,
    year: sessionUser.year ?? new Date().getFullYear(),
    createdAt: sessionUser.createdAt,
    updatedAt: sessionUser.updatedAt,
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const session = useSession()
  // The Better Auth org plugin exposes `useActiveMember` as a hook on the
  // client (atom keys are transformed to `use<Capitalized>` hooks). When the
  // user has no active organization yet, `data` is null.
  const activeMember = (authClient as any).useActiveMember?.() as {
    data: { role?: string } | null | undefined
    isPending: boolean
  } | undefined

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSwitchingBranch, setIsSwitchingBranch] = useState(false)

  // The active branch (organization) scopes every list/detail query on the
  // backend via the session cookie, but the branch id is NOT part of any
  // query key — so switching branches leaves stale data in the cache. Drop
  // the whole cache whenever the active branch actually changes so every
  // mounted view refetches for the new branch.
  const activeOrgId =
    (session.data?.session as { activeOrganizationId?: string } | undefined)
      ?.activeOrganizationId ?? null
  const prevOrgRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!session.data) return
    const prev = prevOrgRef.current
    if (prev === undefined) {
      // First observation after the session loads — record the baseline
      // without invalidating (a fresh load already fetches the right branch).
      prevOrgRef.current = activeOrgId
      return
    }
    if (prev !== activeOrgId) {
      prevOrgRef.current = activeOrgId
      setIsSwitchingBranch(true)
      // invalidateQueries resolves once the new branch's active queries have
      // refetched — keep the loading overlay up until that settles.
      queryClient
        .invalidateQueries()
        .catch(() => {})
        .finally(() => setIsSwitchingBranch(false))
    }
  }, [activeOrgId, session.data, queryClient])

  // Switch the active branch. Raises the global overlay immediately so it
  // covers the setActive round-trip; the effect above keeps it up while the
  // new branch's data refetches and then hides it.
  const switchBranch = async (organizationId: string): Promise<boolean> => {
    if (organizationId === activeOrgId) return true
    setIsSwitchingBranch(true)
    try {
      const { error } = await authClient.organization.setActive({ organizationId })
      if (!error) return true
      toast.error(error.message || 'No se pudo cambiar de sucursal')
    } catch {
      toast.error('No se pudo cambiar de sucursal')
    }
    setIsSwitchingBranch(false)
    return false
  }

  const user = useMemo(
    () => mapSessionUser(session.data?.user, activeMember?.data ?? null),
    [session.data?.user, activeMember?.data],
  )
  const isAuthenticated = !!user
  // A user with an active branch gets their role from the branch member, which
  // may still be loading after the session resolves — mapSessionUser would then
  // fall back to 'staff', flashing wrong nav / a spurious "Acceso denegado".
  // Stay in the loading state until the member resolves (system_admin doesn't
  // need it; a user with no active branch goes to the selection page).
  const needsBranchRole =
    !!session.data &&
    !!activeOrgId &&
    (session.data.user as { role?: string } | undefined)?.role !== 'system_admin'
  const isLoading =
    session.isPending || (needsBranchRole && activeMember?.isPending === true)

  const login = async (credentials: LoginRequest): Promise<boolean> => {
    const { data, error } = await authClient.signIn.email({
      email: credentials.email,
      password: credentials.password,
    })

    if (error) {
      toast.error(error.message || 'No se pudo iniciar sesión. Verifica tus credenciales.')
      return false
    }

    const firstName = (data?.user as any)?.firstName ?? data?.user?.name?.split(' ')[0] ?? ''
    toast.success(`¡Bienvenido de vuelta${firstName ? `, ${firstName}` : ''}!`)
    return true
  }

  const register = async (userData: RegisterRequest): Promise<boolean> => {
    const { data, error } = await authClient.signUp.email({
      email: userData.email,
      password: userData.password,
      name: `${userData.firstName} ${userData.lastName}`.trim(),
      firstName: userData.firstName,
      lastName: userData.lastName,
      phone: userData.phone,
    } as any)

    if (error) {
      toast.error(error.message || 'No se pudo completar el registro. Intenta de nuevo.')
      return false
    }

    const firstName = (data?.user as any)?.firstName ?? userData.firstName
    toast.success(`¡Bienvenido a St. George${firstName ? `, ${firstName}` : ''}!`)
    return true
  }

  const logout = async (): Promise<void> => {
    try {
      await authClient.signOut()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Wipe all cached queries so a future login doesn't see stale data
      // from the previous user.
      queryClient.clear()
      toast.success('Sesión cerrada correctamente')
      navigate('/login', { replace: true })
    }
  }

  const updateUser = async (updatedData: Partial<User>): Promise<boolean> => {
    const payload: Record<string, any> = {}
    if (updatedData.firstName !== undefined) payload.firstName = updatedData.firstName
    if (updatedData.lastName !== undefined) payload.lastName = updatedData.lastName
    if (updatedData.phone !== undefined) payload.phone = updatedData.phone
    if (updatedData.profilePicture !== undefined) {
      payload.profilePicture = updatedData.profilePicture
      payload.image = updatedData.profilePicture
    }
    if (updatedData.preferences !== undefined) {
      payload.preferences = JSON.stringify(updatedData.preferences)
    }
    if (updatedData.firstName !== undefined || updatedData.lastName !== undefined) {
      const fn = updatedData.firstName ?? user?.firstName ?? ''
      const ln = updatedData.lastName ?? user?.lastName ?? ''
      payload.name = `${fn} ${ln}`.trim()
    }

    const { error } = await authClient.updateUser(payload)
    if (error) {
      toast.error(error.message || 'No se pudo actualizar el perfil')
      return false
    }

    toast.success('Perfil actualizado correctamente')
    await session.refetch()
    return true
  }

  const checkAuth = async (): Promise<void> => {
    await session.refetch()
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    checkAuth,
    isSwitchingBranch,
    activeBranchId: activeOrgId,
    switchBranch,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isSwitchingBranch && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-sm">
          <Spinner className="size-10 text-primary" />
          <p className="text-sm font-medium text-muted-foreground">Cambiando de sucursal…</p>
        </div>
      )}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider')
  }
  return context
}

interface RequireRoleProps {
  children: ReactNode
  roles: Array<'admin' | 'teacher' | 'staff'>
  fallback?: ReactNode
}

export const RequireRole: React.FC<RequireRoleProps> = ({
  children,
  roles,
  fallback = (
    <div className="py-8 text-center text-sm text-muted-foreground">
      Acceso denegado. Permisos insuficientes.
    </div>
  ),
}) => {
  const { user } = useAuth()

  if (!user || !roles.includes(user.role)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

export default AuthContext
